/**
 * MusicPlayer 音频图（Web Audio API + TUAC/均衡）
 * 抽离自 MusicPlayer.js，返回 initAudioContext, resumeAudioContext, ensureMediaSource, ensureEqualizerNodes, rebuildAudioGraph。
 */
import { useCallback } from 'react';
import TuacAudioEngine from './tuacAudioEngine';
import { disconnectNodeSafely, getEqualizerGains, isIOSDevice } from './audioGraph';
import { mpWarn, sendClientDebugToServer } from './constants';

const DRY_WET_RAMP_TC = 0.08;
/** Direct 切换时淡出/淡入时长，避免噼啪声 */
const DIRECT_SWITCH_RAMP_MS = 6;
const DIRECT_SWITCH_WAIT_MS = 10;
/** outputGain 滑块 0–100 转为线性增益（与 index.html 一致：50=1.0，100=4.0） */
function outputGainSliderToLinear(val) {
  const v = Number(val);
  if (!Number.isFinite(v) || v <= 0) return 0;
  if (v <= 50) return v / 50;
  return Math.min(4, 1 + (3 * (v - 50)) / 50);
}
/** 临时测试：仅保留环境/空间效果，关闭其他音效 */
const ENVIRONMENT_ONLY_TEST = false;

/**
 * @param {object} opts
 * @param {React.MutableRefObject<AudioContext|null>} opts.audioContextRef
 * @param {React.MutableRefObject<object>} opts.mediaSourceRefs - { audio, video }
 * @param {React.MutableRefObject<object|null>} opts.tuacEngineRef
 * @param {React.MutableRefObject<BiquadFilterNode|null>} opts.bassShelfRef
 * @param {React.MutableRefObject<GainNode|null>} opts.dryGainRef
 * @param {React.MutableRefObject<GainNode|null>} opts.wetGainRef
 * @param {React.MutableRefObject<GainNode|null>} opts.mergeRef
 * @param {React.MutableRefObject<string|null>} opts.activeMediaTypeRef
 * @param {React.MutableRefObject<object|null>} opts.fidelityRef
 * @param {React.RefObject<HTMLAudioElement|null>} opts.audioRef
 * @param {React.RefObject<HTMLVideoElement|null>} opts.videoRef
 * @param {[GainNode[]|null, React.Dispatch]} opts.gainNodesState - [gainNodes, setGainNodes]
 * @param {AudioContext|null} opts.audioContext - 外部持有的 context（可选）
 * @param {React.Dispatch<AudioContext|null>} opts.setAudioContext
 * @param {string} opts.equalizerPreset
 * @param {boolean} opts.surroundEnabled
 * @param {number} opts.surroundIntensity
 * @param {boolean} opts.distortionEnabled
 * @param {number} opts.distortionIntensity
 * @param {number} opts.bassLevel
 * @param {number} opts.reverbLevel
 * @param {string} opts.environmentMode
 * @param {boolean} opts.spaceModeEnabled - 空间扩展/空间模式开关
 * @param {boolean} opts.bassEnabled - 虚拟低音开关
 * @param {boolean} opts.stabilityMode - 稳定播放模式（更大缓冲/更稳）
 * @param {object} opts.tuacParams - tuac_core.inf [tuac] 段参数（缺省由 getHardcodedTuacParams 提供）
 * @param {React.MutableRefObject<AudioQualityMonitor|null>} opts.audioQualityMonitorRef - 音频质量监控器
 * @param {React.MutableRefObject<boolean>} opts.hasMonitoredCurrentSong - 是否已监控当前歌曲
 * @param {function} opts.setTuacAutoDisabled - 设置 TUAC 自动禁用状态
 * @param {boolean} opts.tuacAutoDisabled - TUAC 是否因质量问题自动禁用
 */
export function useAudioGraph({
  audioContextRef,
  mediaSourceRefs,
  tuacEngineRef,
  analyserRef,
  bassShelfRef,
  dryGainRef,
  wetGainRef,
  mergeRef,
  activeMediaTypeRef,
  fidelityRef,
  audioRef,
  videoRef,
  gainNodesState,
  audioContext,
  setAudioContext,
  equalizerPreset,
  surroundEnabled,
  surroundIntensity,
  distortionEnabled,
  distortionIntensity,
  bassLevel,
  reverbLevel,
  environmentMode,
  spaceModeEnabled,
  bassEnabled,
  stabilityMode,
  tuacParams = {},
  // 音频质量监控相关参数
  audioQualityMonitorRef = null,
  hasMonitoredCurrentSong = null,
  setTuacAutoDisabled = null,
  tuacAutoDisabled = false
}) {
  const [gainNodes, setGainNodes] = gainNodesState;

  const initAudioContext = useCallback(() => {
    if (audioContextRef.current) return audioContextRef.current;
    if (audioContext) {
      audioContextRef.current = audioContext;
      return audioContext;
    }
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      // 优先“不断流/不咔咔”：音乐播放对延迟不敏感，使用更大的输出缓冲更稳。
      // - latencyHint: 'playback' 通常会增大内部缓冲，减少 AudioWorklet underrun
      // - 若浏览器不支持该选项则自动回退到默认构造
      let ctx = null;
      try {
        const latencyHint = stabilityMode ? 'playback' : 'interactive';
        ctx = new AudioContextClass({ latencyHint });
      } catch (_) {
        ctx = new AudioContextClass();
      }
      audioContextRef.current = ctx;
      setAudioContext(ctx);
      return ctx;
    } catch (error) {
      console.error('AudioContext not supported:', error);
      return null;
    }
  }, [audioContext, audioContextRef, setAudioContext, stabilityMode]);

  const resumeAudioContext = useCallback(async () => {
    const ctx = initAudioContext();
    if (ctx && ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch (_) {}
    }
  }, [initAudioContext]);

  /**
   * 关闭并重建 AudioContext（用于切换 latencyHint / 稳定模式）。
   * 注意：MediaElementSource 绑定在 context 上，因此需要整体重置。
   */
  const resetAudioContext = useCallback(async () => {
    const ctx = audioContextRef.current || audioContext;
    if (!ctx) return;

    // 尽量断开所有节点，避免旧图残留
    try {
      Object.values(mediaSourceRefs.current || {}).forEach(disconnectNodeSafely);
    } catch (_) {}
    try {
      // source.disconnect() 在 rebuild 时做过，这里额外兜底
      if (mediaSourceRefs.current) {
        mediaSourceRefs.current.audio = null;
        mediaSourceRefs.current.video = null;
      }
    } catch (_) {}

    try {
      if (gainNodes && gainNodes.length) gainNodes.forEach(disconnectNodeSafely);
    } catch (_) {}
    try { setGainNodes(null); } catch (_) {}

    try { if (fidelityRef.current?.outputGain) disconnectNodeSafely(fidelityRef.current.outputGain); } catch (_) {}
    try { fidelityRef.current = null; } catch (_) {}

    try { if (dryGainRef.current) disconnectNodeSafely(dryGainRef.current); } catch (_) {}
    try { if (wetGainRef.current) disconnectNodeSafely(wetGainRef.current); } catch (_) {}
    try { if (mergeRef.current) disconnectNodeSafely(mergeRef.current); } catch (_) {}
    try { dryGainRef.current = null; wetGainRef.current = null; mergeRef.current = null; } catch (_) {}

    try { if (tuacEngineRef.current?.output) disconnectNodeSafely(tuacEngineRef.current.output); } catch (_) {}
    try { tuacEngineRef.current?.dispose?.(); } catch (_) {}
    try { tuacEngineRef.current = null; } catch (_) {}

    try { if (bassShelfRef.current) disconnectNodeSafely(bassShelfRef.current); } catch (_) {}
    try { bassShelfRef.current = null; } catch (_) {}

    try { if (analyserRef?.current) disconnectNodeSafely(analyserRef.current); } catch (_) {}
    try { if (analyserRef) analyserRef.current = null; } catch (_) {}

    try { activeMediaTypeRef.current = null; } catch (_) {}

    try {
      await ctx.close();
    } catch (_) {}

    try {
      if (audioContextRef.current === ctx) audioContextRef.current = null;
    } catch (_) {}
    try { setAudioContext(null); } catch (_) {}
  }, [
    audioContextRef,
    audioContext,
    setAudioContext,
    mediaSourceRefs,
    gainNodes,
    setGainNodes,
    fidelityRef,
    dryGainRef,
    wetGainRef,
    mergeRef,
    tuacEngineRef,
    bassShelfRef,
    analyserRef,
    activeMediaTypeRef
  ]);

  const ensureMediaSource = useCallback((ctx, mediaEl, type) => {
    if (!ctx || !mediaEl) return null;
    const existing = mediaSourceRefs.current[type];
    // 关键：MediaElementSourceNode 绑定在 AudioContext 上，跨 context 复用会导致 connect() 报错（部分安卓更严格）
    if (existing) {
      try {
        if (existing.context === ctx) return existing;
      } catch (_) {}
      // context 已变：丢弃旧 source（disconnect 兜底），重新创建
      try { existing.disconnect(); } catch (_) {}
      try { mediaSourceRefs.current[type] = null; } catch (_) {}
    }
    try {
      const source = ctx.createMediaElementSource(mediaEl);
      mediaSourceRefs.current[type] = source;
      return source;
    } catch (error) {
      console.warn('[Audio] createMediaElementSource failed:', error);
      return null;
    }
  }, [mediaSourceRefs]);

  const ensureEqualizerNodes = useCallback((ctx, gains) => {
    if (!ctx) return null;
    let nodes = gainNodes;
    const needsRecreate = !nodes || nodes.some(node => node.context !== ctx);
    if (needsRecreate) {
      nodes = gains.map(gain => {
        const node = ctx.createGain();
        node.gain.value = 1 + (gain / 10);
        return node;
      });
      setGainNodes(nodes);
      return nodes;
    }
    nodes.forEach((node, index) => {
      node.gain.value = 1 + (gains[index] / 10);
    });
    return nodes;
  }, [gainNodes, setGainNodes]);

  const rebuildAudioGraph = useCallback(async (preferredType = null) => {
    const ctx = initAudioContext();
    if (!ctx) return false;
    const mediaType = preferredType || activeMediaTypeRef.current;
    if (!mediaType) return false;
    const mediaEl = mediaType === 'video' ? videoRef.current : audioRef.current;
    if (!mediaEl) return false;

    if (mediaType === 'audio' && isIOSDevice()) {
      activeMediaTypeRef.current = mediaType;
      return true;
    }

    // 若已有输出节点，先淡出再断链，避免 Direct 切换产生噼啪声
    const mergeExisting = mergeRef.current;
    if (mergeExisting && mergeExisting.context === ctx) {
      const tc = DIRECT_SWITCH_RAMP_MS / 1000;
      mergeExisting.gain.setTargetAtTime(0, ctx.currentTime, tc);
      await new Promise((r) => setTimeout(r, DIRECT_SWITCH_WAIT_MS));
    }

    const source = ensureMediaSource(ctx, mediaEl, mediaType);
    if (!source) return false;

    // 参考 TUAC index.html connectEnginePath：第一步 全链路彻底断开（先断开源节点所有输出）
    try {
      source.disconnect();
    } catch (_) {}
    Object.values(mediaSourceRefs.current).forEach(disconnectNodeSafely);
    if (gainNodes && gainNodes.length) {
      gainNodes.forEach(disconnectNodeSafely);
    }
    if (fidelityRef.current?.outputGain) {
      disconnectNodeSafely(fidelityRef.current.outputGain);
    }
    if (dryGainRef.current) disconnectNodeSafely(dryGainRef.current);
    if (wetGainRef.current) disconnectNodeSafely(wetGainRef.current);
    if (mergeRef.current) disconnectNodeSafely(mergeRef.current);
    if (tuacEngineRef.current?.output) disconnectNodeSafely(tuacEngineRef.current.output);
    if (bassShelfRef.current) disconnectNodeSafely(bassShelfRef.current);

    // 这些 GainNode 也强绑定在 AudioContext 上；切换/重建 context 后若复用旧节点，会在安卓上 connect() 直接抛错
    const gainsNeedRecreate = (() => {
      try {
        if (!dryGainRef.current || !wetGainRef.current || !mergeRef.current) return true;
        return dryGainRef.current.context !== ctx || wetGainRef.current.context !== ctx || mergeRef.current.context !== ctx;
      } catch (_) {
        return true;
      }
    })();
    if (gainsNeedRecreate) {
      try { if (dryGainRef.current) disconnectNodeSafely(dryGainRef.current); } catch (_) {}
      try { if (wetGainRef.current) disconnectNodeSafely(wetGainRef.current); } catch (_) {}
      try { if (mergeRef.current) disconnectNodeSafely(mergeRef.current); } catch (_) {}
      dryGainRef.current = ctx.createGain();
      wetGainRef.current = ctx.createGain();
      mergeRef.current = ctx.createGain();
    }
    const dryGain = dryGainRef.current;
    const wetGain = wetGainRef.current;
    const merge = mergeRef.current;
    const mergeGainLinear = surroundEnabled
      ? outputGainSliderToLinear(tuacParams.outputGain ?? 60)
      : 1;
    merge.gain.setValueAtTime(0, ctx.currentTime);
    merge.gain.setTargetAtTime(mergeGainLinear, ctx.currentTime, DRY_WET_RAMP_TC);

    // 第二步：根据状态重建物理链路（参考 TUAC connectEnginePath）
    source.connect(dryGain);
    dryGain.connect(merge);
    wetGain.connect(merge);

    if (surroundEnabled) {
      // 模式 B: 引擎处理 (Direct OFF) — Source -> TUAC -> WetGain -> Merge，参数以 tuac_core.inf [tuac] 为底，播放器 overrides 覆盖
      // 三星等机型：AudioContext 在 suspended 时 addModule/connect 可能失败，先 resume
      if (ctx.state === 'suspended') {
        try {
          await ctx.resume();
        } catch (_) {}
      }
      if (!tuacEngineRef.current || tuacEngineRef.current.ctx !== ctx) {
        try {
          tuacEngineRef.current?.dispose?.();
        } catch (_) {}
        tuacEngineRef.current = new TuacAudioEngine(ctx);
      }
      const tuac = tuacEngineRef.current;

      try {
        await tuac.loadEnvironment(environmentMode);
      } catch (err) {
        mpWarn('[TUAC] loadEnvironment failed:', err);
        sendClientDebugToServer('TUAC', 'loadEnvironment failed', err);
      }

      // 兼容性兜底：仅当节点缺失时预判降级；否则以「实际 connect 是否抛错」为准，
      // 避免部分机型（如三星）因 context 比较等误判导致误降级。
      const tuacInput = tuac?.input ?? null;
      const tuacOutput = tuac?.output ?? null;
      const nodeMissing = !tuacInput || !tuacOutput;

      const applyFallback = (reason) => {
        mpWarn('[TUAC]', reason, 'fallback to direct path');
        sendClientDebugToServer('TUAC', `fallback: ${reason}`);
        try {
          const now = Date.now();
          const last = Number(tuac && tuac._fallbackHintAtMs) || 0; // eslint-disable-line no-underscore-dangle
          if (now - last > 15000) {
            // eslint-disable-next-line no-underscore-dangle
            if (tuac) tuac._fallbackHintAtMs = now;
            window.dispatchEvent(new CustomEvent('app:tuac-fallback', {
              detail: { reason: 'worklet_unavailable', feature: 'spatial' }
            }));
          }
        } catch (_) {}
        const t = ctx.currentTime;
        dryGain.gain.setTargetAtTime(1, t, DRY_WET_RAMP_TC);
        wetGain.gain.setTargetAtTime(0, t, DRY_WET_RAMP_TC);
      };

      if (nodeMissing) {
        applyFallback('worklet node not available');
      } else {
        const warmthValue = ENVIRONMENT_ONLY_TEST
          ? 0
          : (distortionEnabled ? distortionIntensity : (tuacParams.warmth ?? 0.3));
        let spatialInput = spaceModeEnabled
          ? ((environmentMode === 'plate' || environmentMode === 'hall')
            ? (surroundIntensity * 0.4)
            : surroundIntensity)
          : 0;
        let nextReverb = spaceModeEnabled ? (reverbLevel ?? tuacParams.reverbLevel ?? 0.05) : 0;
        const nextBass = (bassEnabled && !ENVIRONMENT_ONLY_TEST)
          ? Math.max(0, Math.min(1, bassLevel ?? tuacParams.bassIntensity ?? 0.5))
          : 0;

        tuac.setParams({
          ...tuacParams,
          fidelity: tuacParams.fidelity ?? 0.45,
          warmth: warmthValue,
          thickness: tuacParams.thickness ?? 0.2,
          spatial: spatialInput,
          reverbLevel: nextReverb,
          bassIntensity: nextBass,
          inputGain: tuacParams.inputGain ?? 0.5,
          spatialDeconstruction: tuacParams.spatialDeconstruction ?? true
        });

        try {
          source.connect(tuacInput);
          tuacOutput.connect(wetGain);
          const t = ctx.currentTime;
          dryGain.gain.setTargetAtTime(0, t, DRY_WET_RAMP_TC);
          wetGain.gain.setTargetAtTime(1, t, DRY_WET_RAMP_TC);
        } catch (connectErr) {
          mpWarn('[TUAC] connect failed:', connectErr);
          sendClientDebugToServer('TUAC', 'connect failed', connectErr);
          try {
            if (typeof source.disconnect === 'function') source.disconnect();
          } catch (_) {}
          applyFallback('connect failed');
        }
      }
    } else {
      // 模式 A: 直通 (Direct ON) — 仅 Source -> DryGain -> Merge，merge 增益 1.0 原汁原味
      const t = ctx.currentTime;
      dryGain.gain.setTargetAtTime(1, t, DRY_WET_RAMP_TC);
      wetGain.gain.setTargetAtTime(0, t, DRY_WET_RAMP_TC);
    }

    let currentNode = merge;

    if (equalizerPreset && equalizerPreset !== 'normal') {
      const gains = getEqualizerGains(equalizerPreset);
      const nodes = ensureEqualizerNodes(ctx, gains);
      if (nodes && nodes.length) {
        nodes.forEach(node => {
          currentNode.connect(node);
          currentNode = node;
        });
      }
    }

    // ========== 音频质量监控系统集成 ==========
    // 在频谱分析之前，根据是否启用 TUAC 和自动禁用状态决定连接路径
    const effectiveSurroundEnabled = surroundEnabled && !tuacAutoDisabled;
    let monitoredNode = currentNode;
    
    if (
      audioQualityMonitorRef?.current &&
      hasMonitoredCurrentSong?.current === false &&
      effectiveSurroundEnabled  // 仅在启用 TUAC 时监控
    ) {
      console.log('[rebuildAudioGraph] 启动音频质量监控...');
      hasMonitoredCurrentSong.current = true;
      
      // 质量问题回调
      const onQualityIssue = (issueData) => {
        console.error('[MusicPlayer] ❌ 检测到音频质量问题:', issueData);
        
        // 禁用 TUAC
        if (setTuacAutoDisabled) {
          setTuacAutoDisabled(true);
        }
        
        // 100ms 后重建音频链路（使用直通模式）
        setTimeout(() => {
          console.log('[MusicPlayer] 重建音频链路（直通模式）');
          rebuildAudioGraph(preferredType);
        }, 100);
      };
      
      // 启动监控
      try {
        // 连接链路: currentNode -> monitor -> analyser/destination
        audioQualityMonitorRef.current.start(
          currentNode,         // 从当前节点监控
          ctx.destination,     // 到声卡
          onQualityIssue       // 异常回调
        );
        console.log('[AudioQualityMonitor] ✓ 已启动，监控 30 秒');
      } catch (err) {
        console.error('[AudioQualityMonitor] ✗ 启动失败:', err);
        // 失败时直接使用原始节点，不影响播放
      }
    }

    // 频谱分析：接在 Audio Out 之前（currentNode -> analyser -> destination）
    if (analyserRef) {
      if (!analyserRef.current || analyserRef.current.context !== ctx) {
        analyserRef.current = ctx.createAnalyser();
        analyserRef.current.fftSize = 2048;
        analyserRef.current.smoothingTimeConstant = 0.85;
        analyserRef.current.minDecibels = -90;
        analyserRef.current.maxDecibels = -10;
      }
      monitoredNode.connect(analyserRef.current);
      analyserRef.current.connect(ctx.destination);
    } else {
      monitoredNode.connect(ctx.destination);
    }
    activeMediaTypeRef.current = mediaType;
    return true;
  }, [
    initAudioContext,
    ensureMediaSource,
    ensureEqualizerNodes,
    activeMediaTypeRef,
    mediaSourceRefs,
    fidelityRef,
    analyserRef,
    dryGainRef,
    wetGainRef,
    mergeRef,
    tuacEngineRef,
    bassShelfRef,
    audioRef,
    videoRef,
    gainNodes,
    equalizerPreset,
    surroundEnabled,
    surroundIntensity,
    distortionEnabled,
    distortionIntensity,
    bassLevel,
    reverbLevel,
    environmentMode,
    spaceModeEnabled,
    bassEnabled,
    tuacParams,
    // 音频质量监控相关
    audioQualityMonitorRef,
    hasMonitoredCurrentSong,
    setTuacAutoDisabled,
    tuacAutoDisabled
  ]);

  /** 仅更新效果参数（不重建图），参数以 tuac_core.inf [tuac] 为底 + 播放器 overrides */
  const applyEffectParams = useCallback(() => {
    const tuac = tuacEngineRef.current;
    const merge = mergeRef.current;
    if (!surroundEnabled || !tuac || !merge) return false;
    const ctx = tuac.ctx;
    if (!ctx) return false;
    const warmthValue = ENVIRONMENT_ONLY_TEST
      ? 0
      : (distortionEnabled ? distortionIntensity : (tuacParams.warmth ?? 0.3));
    let spatialInput = spaceModeEnabled
      ? ((environmentMode === 'plate' || environmentMode === 'hall')
        ? (surroundIntensity * 0.5)
        : surroundIntensity)
      : 0;
    let nextReverb = spaceModeEnabled ? (reverbLevel ?? 0) : 0;
    const nextBass = (bassEnabled && !ENVIRONMENT_ONLY_TEST) ? Math.max(0, Math.min(1, bassLevel ?? tuacParams.bassIntensity ?? 0.5)) : 0;
    tuac.setParams({
      ...tuacParams,
      fidelity: tuacParams.fidelity ?? 0.45,
      warmth: warmthValue,
      thickness: tuacParams.thickness ?? 0.2,
      spatial: spatialInput,
      reverbLevel: nextReverb,
      bassIntensity: nextBass,
      inputGain: tuacParams.inputGain ?? 0.5,
      spatialDeconstruction: tuacParams.spatialDeconstruction ?? true
    });
    const mergeGainLinear = outputGainSliderToLinear(tuacParams.outputGain ?? 60);
    merge.gain.setTargetAtTime(mergeGainLinear, ctx.currentTime, DRY_WET_RAMP_TC);
    return true;
  }, [
    surroundEnabled,
    surroundIntensity,
    distortionEnabled,
    distortionIntensity,
    bassLevel,
    reverbLevel,
    environmentMode,
    spaceModeEnabled,
    bassEnabled,
    tuacParams,
    tuacEngineRef,
    mergeRef
  ]);

  /** 仅切换环境/空间（IR），不重建图 */
  const applyEnvironmentMode = useCallback(async () => {
    const tuac = tuacEngineRef.current;
    if (!surroundEnabled || !tuac) return false;
    try {
      await tuac.loadEnvironment(environmentMode);
      return true;
    } catch (_) {
      return false;
    }
  }, [surroundEnabled, environmentMode, tuacEngineRef]);

  return {
    initAudioContext,
    resumeAudioContext,
    resetAudioContext,
    ensureMediaSource,
    ensureEqualizerNodes,
    rebuildAudioGraph,
    applyEffectParams,
    applyEnvironmentMode
  };
}
